/**
 * DocumentsRoute — the routed Documents screen.
 *
 * A thin connective wrapper around `DocumentBrowser`'s tabbed shell, whose rows
 * all come from the server-side `DocumentBrowserContainer`. It reads the active
 * health tab from the query string (`?tab=`) and translates it into the query's
 * `validationStatus` (see `VALIDATION_STATUS_BY_TAB`), pushes route changes for
 * tab/row navigation, and hosts the Upload and Review overlays as LOCAL component
 * state (no URL): those flows are not yet backed, so they open and close in place
 * without touching the router.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Select } from "@jsq/ds/core";
import {
  buildAttributeValueInputs,
  useUploadVaultDocument,
  useUploadVaultDocumentsBatch,
} from "#common/graphql/Document";
import { DocumentBrowser } from "#components/DocumentBrowser";
import { DocumentBrowserContainer } from "#components/DocumentBrowser/container";
import { DocumentReviewQueue } from "#components/DocumentReviewQueue";
import { UploadDocumentModal } from "#components/UploadDocumentModal";
import { skippedFilesMessage } from "#components/UploadDocumentModal/files";
import { useUploadFilePicker } from "#components/UploadDocumentModal/useFilePicker";
import * as styles from "../styles_vanilla.css";
import { groupSpacesByDomain } from "../utils";
import { useVaultData } from "../VaultProvider";
import { VaultPageHeader } from "./VaultPageHeader";
import type { VaultDocumentValidationStatus } from "#common/graphql/Document";
import type { DocumentBrowserTab } from "#components/DocumentBrowser/types";
import type { UploadFileDescriptor } from "#components/UploadDocumentModal/types";

/**
 * Health tab -> the query's `validationStatus`. "All documents" maps to `null`
 * (the field is omitted) rather than to both members: the enum has no `pending`,
 * so enumerating `validated` + `quarantined` would quietly hide documents that
 * are still pending. Module-level, so each array is referentially stable across
 * renders — the grid's datasource keys off it.
 */
const VALIDATION_STATUS_BY_TAB: Record<
  DocumentBrowserTab,
  VaultDocumentValidationStatus[] | null
> = {
  filed: ["validated"],
  awaiting: ["quarantined"],
  all: null,
};

export const DocumentsRoute = ({ basePath }: { basePath: string }) => {
  const history = useHistory();
  const {
    documents,
    quarantine,
    spaces,
    domains,
    domainOrder,
    globalLib,
    globalIdByKey,
    fields,
    reference,
    showToast,
    hasCapability,
  } = useVaultData();

  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const tab = (search.get("tab") as DocumentBrowserTab) || "filed";
  const validationStatus = VALIDATION_STATUS_BY_TAB[tab] ?? null;
  // 1-based page mirrored to `?page=`; clamps junk / <1 to page 1.
  const page = Math.max(1, Number.parseInt(search.get("page") ?? "1", 10) || 1);
  // Active space filter, mirrored to `?space=`. "" is the "All spaces" scope,
  // which the grid and the column contract both read as null.
  const spaceId = search.get("space") ?? "";
  const activeSpace = spaceId || null;

  // The space filter is grouped by domain, exactly as on the un-routed shell.
  // No per-space document counts here: `VaultProvider` skips its shared
  // documents page on this route (the grid fetches its own), so a count would
  // read 0 for every space rather than being merely stale.
  const domainGroups = useMemo(
    () => groupSpacesByDomain(spaces, domains, domainOrder),
    [spaces, domains, domainOrder],
  );

  // Re-scoping changes what the grid is paging through, so page 1 is the only
  // safe landing spot — same reason a tab change drops `page`.
  const selectSpace = (next: string) =>
    history.push(
      `${basePath}/documents?tab=${tab}${next ? `&space=${encodeURIComponent(next)}` : ""}`,
    );

  // Upload opens the OS picker first; the takeover only mounts once files exist.
  const [uploadFiles, setUploadFiles] = useState<UploadFileDescriptor[] | null>(null);
  const { open: openFilePicker, input: filePickerInput } = useUploadFilePicker(
    setUploadFiles,
    // Superseded by the `onSkip` below, which reports both drop reasons at once.
    undefined,
    true,
    (skipped) => {
      const message = skippedFilesMessage(skipped);
      if (message) showToast(message);
    },
  );
  const { uploadDocument } = useUploadVaultDocument();
  const { uploadDocuments } = useUploadVaultDocumentsBatch();
  const [reviewItemId, setReviewItemId] = useState<string | null>(null);
  // Bumped on upload completion to force the server-side grid to refetch: the
  // page=1 URL reset alone doesn't refresh a grid already sitting on page 1.
  const [refreshToken, setRefreshToken] = useState(0);

  // Badge on the "Documents awaiting filing" tab. Taken from the grid's own
  // `totalCount` for that tab's query rather than a second count query, so
  // nothing asks the server about quarantined documents while another tab is
  // open. The trade-off is that the count is only known once you're on the tab —
  // the badge is hidden until then rather than showing a number we can't back up.
  const [awaitingTotal, setAwaitingTotal] = useState<number | undefined>();
  const isAwaiting = tab === "awaiting";
  // Stable identity per tab: the grid's datasource is rebuilt when this changes.
  const handleTotalCount = useCallback((n: number) => setAwaitingTotal(n), []);

  const reviewItem = quarantine.find((q) => q.id === reviewItemId) ?? null;
  const reviewSpace = reviewItem?.space
    ? (spaces.find((s) => s.id === reviewItem.space) ?? null)
    : null;

  return (
    <>
      <VaultPageHeader title="Documents">
        <div className={styles.topSpaceSelect}>
          <Select
            value={spaceId}
            onChange={(val) => selectSpace(String(val))}
            placeholder="All spaces"
            dividerPosition="all"
            full
            // `label` + `hideLabel` is the DS-sanctioned way to name a select
            // whose label would crowd the topbar; a bare `aria-label` would just
            // land on the wrapper div, since that's where stray props are spread.
            // NOTE: this DS version doesn't surface either as an `aria-label` on
            // the input, so the control has no accessible name yet — hence the
            // `data-testid` for tests. Same gap exists on the un-routed shell.
            label="Filter by space"
            hideLabel
            data-testid="vault-space-filter"
          >
            {[
              <Select.Section key="__all">
                <Select.Option value="">All spaces</Select.Option>
              </Select.Section>,
              ...domainGroups.map((group) => (
                <Select.Section key={group.domain.id} name={group.domain.name}>
                  {group.spaces.map((s) => (
                    <Select.Option key={s.id} value={s.id}>
                      {s.name}
                    </Select.Option>
                  ))}
                </Select.Section>
              )),
            ]}
          </Select>
        </div>
        {hasCapability("can_upload_doc_vault_documents") && (
          <Button variant="primary" icon="upload" onClick={openFilePicker}>
            Upload
          </Button>
        )}
        {filePickerInput}
      </VaultPageHeader>

      <div className={styles.content.wide}>
        <DocumentBrowser
          documents={documents}
          quarantine={quarantine}
          spaces={spaces}
          domains={domains}
          globalLib={globalLib}
          space={activeSpace}
          tab={tab}
          // Switching tabs resets to page 1 (the page param is dropped), and keeps
          // the space scope so the filter survives a tab change.
          onTabChange={(t) =>
            history.push(
              `${basePath}/documents?tab=${t}${
                spaceId ? `&space=${encodeURIComponent(spaceId)}` : ""
              }`,
            )
          }
          onOpenDoc={(id) =>
            history.push({ pathname: `${basePath}/documents/${id}`, search: location.search })
          }
          onOpenReview={(id) => setReviewItemId(id)}
          onCreateCollection={() => history.push(`${basePath}/sharing?tab=collections`)}
          awaitingCount={isAwaiting ? awaitingTotal : undefined}
          // Every tab is served by the cursor-paginated GraphQL grid; the tab
          // itself is the query's `validationStatus` (Filed = validated,
          // Awaiting filing = quarantined, All = unfiltered).
          serverGrid={
            <DocumentBrowserContainer
              spaces={spaces}
              domains={domains}
              globalLib={globalLib}
              globalIdByKey={globalIdByKey}
              space={activeSpace}
              validationStatus={validationStatus}
              // Only the awaiting tab's total feeds a badge; elsewhere the grid
              // has no one to report to.
              onTotalCountChange={isAwaiting ? handleTotalCount : undefined}
              page={page}
              refreshToken={refreshToken}
              // Mirror paging to `?page=`; `replace` keeps the back button from
              // stepping through every visited page.
              onPageChange={(p) => {
                if (!Number.isInteger(p) || p < 1) return;
                history.replace(
                  `${basePath}/documents?tab=${tab}${
                    spaceId ? `&space=${encodeURIComponent(spaceId)}` : ""
                  }&page=${p}`,
                );
              }}
              onOpenDoc={(id) =>
                history.push({
                  pathname: `${basePath}/documents/${id}`,
                  search: location.search,
                })
              }
            />
          }
        />
      </div>

      {uploadFiles && (
        <UploadDocumentModal
          initialFiles={uploadFiles}
          spaces={spaces}
          domains={domains}
          // The routed shell always sits inside the app's ApolloProvider, so the
          // live Client / Entity search components can be used here.
          referenceSearch
          globalLib={globalLib}
          fields={fields}
          reference={reference}
          // Pre-fill the takeover with whatever space is being browsed, so an
          // upload from inside a space lands there by default (still editable).
          browseSpace={activeSpace}
          globalIdByKey={globalIdByKey}
          onClose={() => setUploadFiles(null)}
          onToast={showToast}
          onAdd={() => {
            setUploadFiles(null);
          }}
          // Single file: createVaultDocument -> PUT bytes -> finalizeVaultDocument.
          onUploadFile={async ({ file, spaceId, attributeValues, onProgress }) => {
            const document = await uploadDocument({
              spaceId,
              file,
              attributeValues: buildAttributeValueInputs(attributeValues),
              // Multipart reports per-part; single-part jumps straight to 1.
              onProgress,
            });
            return { publicRefId: document?.publicRefId, status: document?.status };
          }}
          // 2+ files: one createVaultDocumentsBatch -> PUT each -> finalize batch.
          // The hook reports one result per request in order, so a document the
          // batch failed to finalize arrives carrying `error` — passed straight
          // through, since the takeover pairs results to files positionally.
          onUploadFiles={async (requests, onProgress) => {
            const results = await uploadDocuments({
              items: requests.map((r) => ({
                spaceId: r.spaceId,
                file: r.file,
                attributeValues: buildAttributeValueInputs(r.attributeValues),
              })),
              onProgress,
            });
            return results.map((r) => ({
              publicRefId: r.publicRefId,
              status: r.status,
              error: r.error,
            }));
          }}
          onUploadComplete={(results) => {
            setUploadFiles(null);
            // Bring the grid in line with what was just filed.
            history.replace(`${basePath}/documents?tab=${tab}&page=1`);
            // The URL reset doesn't refetch a server-side grid already on page 1,
            // so force it to purge its cache and reload the newly filed rows.
            // On the awaiting tab that refetch also re-reports the badge count.
            setRefreshToken((n) => n + 1);
            // onUploadComplete only fires when every file succeeded, so results.length
            // is the success count. The batch path can also call it with [] on a
            // no-op retry (every file already filed), so guard on length > 0.
            if (results.length > 0) {
              const count = results.length;
              showToast(`Uploaded ${count} document${count === 1 ? "" : "s"}`);
            }
          }}
        />
      )}

      {reviewItem && (
        <DocumentReviewQueue
          item={reviewItem}
          space={reviewSpace}
          globalLib={globalLib}
          reference={reference}
          onResolve={() => setReviewItemId(null)}
          onClose={() => setReviewItemId(null)}
        />
      )}
    </>
  );
};
